import type { ChatMessage } from "@/model/csr-chat";
import { MessageType } from "@/model/csr-chat";
import {
  CSR_MESSAGE_GROUP_THRESHOLD_MS,
  CSR_ROLE_BOT,
  CSR_ROLE_MEMBER,
} from "@/services/csr-chat/csr-chat-constants";

export const VALID_FILE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",
] as const;

export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

export const isValidFile = (file: File): boolean =>
  (VALID_FILE_TYPES as readonly string[]).includes(file.type) &&
  file.size <= MAX_FILE_SIZE_BYTES;

export function getBubblePosition(
  messages: ChatMessage[],
  index: number,
): "single" | "first" | "middle" | "last" {
  const msg = messages[index];
  const prev = messages[index - 1];
  const next = messages[index + 1];

  if (msg.role === CSR_ROLE_BOT && msg.type === MessageType.MESSAGE) {
    return "single";
  }

  const withinThreshold = (a: ChatMessage, b: ChatMessage) =>
    Math.abs(
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    ) <= CSR_MESSAGE_GROUP_THRESHOLD_MS;

  const groupedWithPrev =
    Boolean(prev) && prev.role === msg.role && withinThreshold(prev, msg);
  const groupedWithNext =
    Boolean(next) && next.role === msg.role && withinThreshold(msg, next);

  if (!groupedWithPrev && !groupedWithNext) return "single";
  if (!groupedWithPrev && groupedWithNext) return "first";
  if (groupedWithPrev && groupedWithNext) return "middle";
  return "last";
}

export function buildPendingFileMessage(
  file: File,
  tempMessageId: string,
  channelId: string,
  username: string,
): ChatMessage {
  return {
    _id: tempMessageId,
    chatChannel: channelId,
    createdAt: new Date().toISOString(),
    username,
    role: CSR_ROLE_MEMBER,
    type: MessageType.FILE,
    message: JSON.stringify({
      originalName: file.name,
      fileType: file.type,
      uploadFile: true,
      size: file.size,
      tempMessageId,
    }),
    deleted: false,
  };
}

export const isMessageSeen = (msg: ChatMessage, username: string): boolean =>
  Boolean(msg.receiver?.some((r) => r.username === username));
